/**
 * BO by Time — exec-only booking-office throughput & staffing by hour.
 *
 * The ticket-sales analogue of Outlets by Time. Reuses execOutletsModel.ts
 * verbatim: with no cost of goods (tickets carry none), "profit" is the
 * contribution left after the booking-office staffing / utilities / maintenance
 * / management pot — a fixed-for-the-day cost spread across the hours each
 * office actually traded. Physical offices only (walk-up sell-time).
 *
 * Staffing is set PER OFFICE: "All offices" edits the default every office
 * inherits; selecting an office edits that office's own override.
 *
 * The API returns sales FACTS only; everything here is derived, so dragging a
 * cost % re-renders instantly.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import writeXlsxFile from "write-excel-file/browser";
import "./booking-offices.css";
import {
  ApiSeason, ApiDayRow, OutletModel, SlotBucket, Bucket,
  buildOutletModels, buildDayModels, foldSlots, foldedSlotLabel, slotShortLabel,
  resolveCosts, outletStandings, standingLabel, OutletStanding,
  fiscalMonthLabel, EDGE_EARLY, EDGE_LATE,
  money, pct,
} from "./execOutletsModel";

const BO_CATEGORY = "Booking Office";
const OFFICE_ORDER = ["Pickering", "Grosmont", "Goathland", "Whitby"];
function officeRank(loc: string): number {
  const i = OFFICE_ORDER.indexOf(loc);
  return i === -1 ? OFFICE_ORDER.length : i;
}
function currentFiscalYear(): number {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  return m >= 3 ? y + 1 : y;
}
function prettyDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}
function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()];
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [, m, dd] = iso.split("-").map(Number);
  return `${dow} ${dd} ${MONTHS[m - 1]}`;
}
const ALL = "__all__";

function emptyBucket(): Bucket {
  return { net: 0, cogs: 0, fixed: 0, profit: 0, costedNet: 0, items: 0, transactions: 0, dayCount: 0 };
}
function addInto(t: Bucket, s: Bucket) {
  t.net += s.net; t.cogs += s.cogs; t.fixed += s.fixed; t.profit += s.profit;
  t.costedNet += s.costedNet; t.items += s.items; t.transactions += s.transactions; t.dayCount += s.dayCount;
}

interface MonthView extends Bucket { month: number; tradingDays: number; slots: SlotBucket[]; }
interface DayView extends Bucket { date: string; slots: SlotBucket[]; }
interface View { total: Bucket; totalTradingDays: number; months: MonthView[]; slots: SlotBucket[]; }

function aggregateView(selected: OutletModel | null, models: OutletModel[]): View {
  const list = selected ? [selected] : models;
  const monthMap = new Map<number, { agg: Bucket; slots: Map<number, SlotBucket>; tradingDays: number }>();
  const seasonSlots = new Map<number, SlotBucket>();
  const total = emptyBucket();
  for (const o of list) {
    for (const m of o.months) {
      let mm = monthMap.get(m.month);
      if (!mm) { mm = { agg: emptyBucket(), slots: new Map(), tradingDays: 0 }; monthMap.set(m.month, mm); }
      addInto(mm.agg, m);
      // A single office: its own trading days. All offices: the days at least
      // one office was open (max across offices), so it isn't double-counted.
      mm.tradingDays = selected ? m.tradingDays : Math.max(mm.tradingDays, m.tradingDays);
      for (const s of m.slots) {
        const cur = mm.slots.get(s.slot) ?? { ...emptyBucket(), slot: s.slot }; addInto(cur, s); mm.slots.set(s.slot, cur);
        const sc = seasonSlots.get(s.slot) ?? { ...emptyBucket(), slot: s.slot }; addInto(sc, s); seasonSlots.set(s.slot, sc);
      }
    }
    addInto(total, o);
  }
  const months: MonthView[] = [...monthMap.entries()]
    .map(([month, mm]) => ({ month, ...mm.agg, tradingDays: mm.tradingDays, slots: [...mm.slots.values()].sort((a, b) => a.slot - b.slot) }))
    .sort((a, b) => a.month - b.month);
  const totalTradingDays = months.reduce((a, m) => a + m.tradingDays, 0);
  const slots = [...seasonSlots.values()].sort((a, b) => a.slot - b.slot);
  return { total, totalTradingDays, months, slots };
}

function buildDayView(rows: ApiDayRow[], selected: OutletModel | null, models: OutletModel[], monthIdx: number): DayView[] {
  const list = selected ? [selected] : models;
  const byDate = new Map<string, { agg: Bucket; slots: Map<number, SlotBucket> }>();
  for (const o of list) {
    const mNet = o.months.find((m) => m.month === monthIdx)?.net ?? 0;
    const days = buildDayModels(rows, o, "modelled", 0, mNet); // filters to this office internally
    for (const d of days) {
      let dd = byDate.get(d.date);
      if (!dd) { dd = { agg: emptyBucket(), slots: new Map() }; byDate.set(d.date, dd); }
      addInto(dd.agg, d);
      for (const s of d.slots) {
        const cur = dd.slots.get(s.slot) ?? { ...emptyBucket(), slot: s.slot }; addInto(cur, s); dd.slots.set(s.slot, cur);
      }
    }
  }
  return [...byDate.entries()]
    .map(([date, dd]) => ({ date, ...dd.agg, slots: [...dd.slots.values()].sort((a, b) => a.slot - b.slot) }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

interface TxnState {
  loading: boolean; error: string | null; available: boolean;
  total: number; totalNet: number; capped: boolean;
  rows: { date: string | null; time: string; trans_no: string; location: string; net: number; items: number }[];
}
interface BdState {
  loading: boolean; error: string | null; available: boolean;
  rows: { analysis: string; category: string; net: number; items: number; transactions: number }[];
}
interface ModalState { label: string; bucket: Bucket; txn: TxnState | null; bd: BdState | null; }
interface CostDraft { staffing: string; utilities: string; maintenance: string; management: string; staffCount: string; staffRate: string; }

export function BOByTime() {
  const [data, setData] = useState<ApiSeason | null>(null);
  const [fiscalYear, setFiscalYear] = useState<number>(() => currentFiscalYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outlet, setOutlet] = useState<string>(ALL);
  // Booking offices trade ~07:30–16:30 and their busiest hour is the 08:30
  // pre-departure rush, which the retail 09:30 window would fold into an edge
  // bucket. Default to every clock hour so the real profile shows immediately.
  const [allHours, setAllHours] = useState(true);
  const [draft, setDraft] = useState<CostDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [expandedMonth, setExpandedMonth] = useState<number | null>(null);
  const [dayRows, setDayRows] = useState<Record<number, ApiDayRow[]>>({});
  const [daysLoading, setDaysLoading] = useState(false);

  const load = useCallback((fy: number) => {
    setLoading(true);
    setError(null);
    fetch(`/api/dashboards/booking-offices/by-time?fiscalYear=${fy}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Request failed (${res.status})`); }
        return res.json() as Promise<ApiSeason>;
      })
      .then((d) => { setData(d); if (d.fiscalYear !== fy) setFiscalYear(d.fiscalYear); setDayRows({}); setExpandedMonth(null); })
      .catch((e) => { setError(e instanceof Error ? e.message : String(e)); setData(null); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(fiscalYear); }, [fiscalYear, load]);

  const models = useMemo(() => (data ? buildOutletModels(data, "modelled") : []), [data]);
  const standings = useMemo(() => outletStandings(models), [models]);
  const offices = useMemo(
    () => [...models].sort((a, b) => officeRank(a.location) - officeRank(b.location) || a.location.localeCompare(b.location)),
    [models]
  );
  const overriddenLocs = useMemo(() => new Set((data?.cost_model ?? []).filter((r) => r.location).map((r) => r.location as string)), [data]);

  const selected = outlet === ALL ? null : models.find((m) => m.location === outlet) ?? null;
  const costScope = outlet === ALL ? "" : outlet;
  const view = useMemo(() => aggregateView(selected, models), [selected, models]);

  // Reset the cost draft whenever the scope or data changes.
  useEffect(() => {
    if (!data) { setDraft(null); return; }
    const c = resolveCosts(data.cost_model, BO_CATEGORY, costScope);
    setDraft({
      staffing: String(c.staffing), utilities: String(c.utilities), maintenance: String(c.maintenance),
      management: String(c.management), staffCount: c.staffCount == null ? "" : String(c.staffCount),
      staffRate: c.staffCostPerHour == null ? "" : String(c.staffCostPerHour),
    });
    setSaveErr(null);
  }, [data, costScope]);

  // Reset the day drill when the office changes (day rows are office-agnostic
  // but the aggregation depends on scope, so just close it).
  useEffect(() => { setExpandedMonth(null); }, [outlet]);

  const staffHourlyDraft = Boolean(draft && Number(draft.staffCount) > 0 && Number(draft.staffRate) > 0);
  const marginDraft = draft ? 100 - Number(draft.staffing || 0) - Number(draft.utilities || 0) - Number(draft.maintenance || 0) - Number(draft.management || 0) : 0;
  const scopeHasOverride = Boolean(costScope) && overriddenLocs.has(costScope);

  const saveCosts = () => {
    if (!draft) return;
    setSaving(true); setSaveErr(null);
    fetch(`/api/dashboards/booking-offices/costs`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        location: costScope,
        staffing_pct: Number(draft.staffing || 0), utilities_pct: Number(draft.utilities || 0),
        maintenance_pct: Number(draft.maintenance || 0), management_pct: Number(draft.management || 0),
        staff_count: draft.staffCount === "" ? null : Number(draft.staffCount),
        staff_cost_per_hour: draft.staffRate === "" ? null : Number(draft.staffRate),
      }),
    })
      .then(async (res) => { if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Save failed (${res.status})`); } return res.json(); })
      .then(() => load(fiscalYear))
      .catch((e) => setSaveErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setSaving(false));
  };
  const resetOverride = () => {
    if (!costScope) return;
    setSaving(true); setSaveErr(null);
    fetch(`/api/dashboards/booking-offices/costs?location=${encodeURIComponent(costScope)}`, { method: "DELETE", credentials: "include" })
      .then(async (res) => { if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Reset failed (${res.status})`); } })
      .then(() => load(fiscalYear))
      .catch((e) => setSaveErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setSaving(false));
  };

  const toggleMonth = (month: number) => {
    const next = expandedMonth === month ? null : month;
    setExpandedMonth(next);
    if (next != null && !dayRows[next]) {
      setDaysLoading(true);
      fetch(`/api/dashboards/booking-offices/by-time/days?fiscalYear=${fiscalYear}&month=${next}`, { credentials: "include" })
        .then(async (res) => { if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Request failed (${res.status})`); } return res.json() as Promise<{ rows: ApiDayRow[] }>; })
        .then((j) => setDayRows((cur) => ({ ...cur, [next]: j.rows ?? [] })))
        .catch(() => setDayRows((cur) => ({ ...cur, [next]: [] })))
        .finally(() => setDaysLoading(false));
    }
  };

  const dayView = useMemo(
    () => (expandedMonth != null && dayRows[expandedMonth] ? buildDayView(dayRows[expandedMonth], selected, models, expandedMonth) : []),
    [expandedMonth, dayRows, selected, models]
  );

  // Open the hour-detail modal, fetching the transactions for that hour (a
  // single slot, or an edge range) scoped to a month or a specific day.
  const openDetail = (opts: { slot: number; label: string; bucket: Bucket; month: number | null; date: string | null }) => {
    const { slot, label, bucket, month, date } = opts;
    const isEdge = slot === EDGE_EARLY || slot === EDGE_LATE;
    setModal({
      label, bucket,
      txn: { loading: true, error: null, available: false, total: 0, totalNet: 0, capped: false, rows: [] },
      bd: { loading: true, error: null, available: false, rows: [] },
    });
    const locParam = selected ? selected.location : offices.map((o) => o.location).join("|");
    const qs = new URLSearchParams({ locations: locParam, fiscalYear: String(fiscalYear) });
    if (isEdge) qs.set("edge", slot === EDGE_EARLY ? "early" : "late"); else qs.set("slot", String(slot));
    if (date) qs.set("date", date); else if (month != null) qs.set("month", String(month));
    const q = qs.toString();
    // Ticket-type breakdown — what was sold this hour (explains a negative).
    fetch(`/api/dashboards/booking-offices/by-time/breakdown?${q}`, { credentials: "include" })
      .then(async (res) => { if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Request failed (${res.status})`); } return res.json(); })
      .then((r) => setModal((cur) => cur && cur.label === label ? { ...cur, bd: { loading: false, error: null, available: Boolean(r.available), rows: r.rows ?? [] } } : cur))
      .catch((e) => setModal((cur) => cur && cur.label === label ? { ...cur, bd: { loading: false, error: e instanceof Error ? e.message : String(e), available: false, rows: [] } } : cur));
    // Individual transactions.
    fetch(`/api/dashboards/booking-offices/by-time/transactions?${q}`, { credentials: "include" })
      .then(async (res) => { if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Request failed (${res.status})`); } return res.json(); })
      .then((r) => setModal((cur) => cur && cur.label === label ? { ...cur, txn: { loading: false, error: null, available: Boolean(r.available), total: r.total ?? 0, totalNet: r.totalNet ?? 0, capped: Boolean(r.capped), rows: r.rows ?? [] } } : cur))
      .catch((e) => setModal((cur) => cur && cur.label === label ? { ...cur, txn: { loading: false, error: e instanceof Error ? e.message : String(e), available: false, total: 0, totalNet: 0, capped: false, rows: [] } } : cur));
  };

  const scopeLabel = outlet === ALL ? "all offices" : outlet;

  return (
    <main className="bt-wrap">
      <div className="bt-head">
        <div>
          <h1>BO by Time</h1>
          <p className="bt-sub">
            Booking-office ticket takings by hour, with a per-office staffing cost model. Walk-up counter sales only
            (Pickering, Grosmont, Goathland, Whitby). Tickets have no cost of goods, so "contribution" is what is left
            after staffing, utilities, maintenance and management — a pot spread across the hours each office traded, so a
            quiet hour shows red while the office is fine for the year.
          </p>
        </div>
        <div className="bt-refresh">Last refreshed {prettyDateTime(data?.last_refresh ?? null)}</div>
      </div>

      <div className="bt-toolbar">
        <label className="bt-field">
          <span>Season</span>
          <select value={fiscalYear} onChange={(e) => setFiscalYear(Number(e.target.value))}>
            {(data?.years ?? [fiscalYear]).map((y) => (<option key={y} value={y}>{`${y - 1}/${String(y).slice(-2)}`}</option>))}
          </select>
        </label>
        <button type="button" className={"bt-pill" + (allHours ? " on" : "")} onClick={() => setAllHours((v) => !v)} aria-pressed={allHours}>
          {allHours ? "All hours" : "Trading hours (09:30–17:30)"}
        </button>
        <span className="bt-spacer" />
        <button type="button" className="bt-btn ghost" onClick={() => data && exportExcel(view, data.fiscalYearLabel, scopeLabel, allHours)} disabled={!data || models.length === 0}>⬇ Excel</button>
      </div>

      {error ? (
        <div className="bt-empty err">Couldn’t load BO by Time: {error}</div>
      ) : loading ? (
        <div className="bt-empty">Loading…</div>
      ) : !data || models.length === 0 ? (
        <div className="bt-empty">No hourly booking-office data yet — the refresh may not have run for this season.</div>
      ) : (
        <>
          {/* Office tabs */}
          <div className="bt-tabs" role="tablist" aria-label="Booking offices">
            <button type="button" role="tab" aria-selected={outlet === ALL} className={"bt-tab" + (outlet === ALL ? " active" : "")} onClick={() => setOutlet(ALL)}>
              All offices
            </button>
            {offices.map((o) => {
              const s: OutletStanding = standings.get(`${o.category}|${o.location}`) ?? null;
              const lbl = standingLabel(s);
              return (
                <button key={o.location} type="button" role="tab" aria-selected={outlet === o.location}
                  className={"bt-tab" + (outlet === o.location ? " active" : "")} onClick={() => setOutlet(o.location)}>
                  {o.location}
                  {overriddenLocs.has(o.location) && <span className="bt-custom" title="Has its own staffing override">custom</span>}
                  {lbl && <span className="bt-standing" data-s={s ?? undefined}>{lbl}</span>}
                </button>
              );
            })}
          </div>

          {/* KPIs */}
          <div className="bt-kpis">
            <Kpi k="Ticket revenue" v={money(view.total.net)} />
            <Kpi k="Fixed cost" v={money(view.total.fixed)} />
            <Kpi k="Contribution" v={money(view.total.profit)} neg={view.total.profit < 0} />
            <Kpi k="Margin" v={pct(view.total.net > 0 ? (view.total.profit / view.total.net) * 100 : null)} neg={view.total.profit < 0} />
            <Kpi k="Transactions" v={view.total.transactions.toLocaleString("en-GB")} />
            <Kpi k="Avg £/txn" v={money(view.total.transactions > 0 ? view.total.net / view.total.transactions : 0)} />
          </div>

          {/* Cost editor (per office) */}
          {draft && (
            <div className="bt-cost">
              <div className="bt-cost-head">
                <span>
                  Staffing cost model — {outlet === ALL ? "default (all offices)" : outlet}
                  {outlet !== ALL && <em style={{ fontWeight: 400, color: "#6a6a6a" }}> · {scopeHasOverride ? "custom override" : "inherits default"}</em>}
                </span>
                <span>Contribution margin {staffHourlyDraft ? "(set by hours)" : `${Math.round(marginDraft * 10) / 10}%`}</span>
              </div>
              <div className="bt-cost-grid">
                <CostField label="Staffing %" value={draft.staffing} dim={staffHourlyDraft} onChange={(v) => setDraft({ ...draft, staffing: v })} />
                <CostField label="Utilities %" value={draft.utilities} onChange={(v) => setDraft({ ...draft, utilities: v })} />
                <CostField label="Maintenance %" value={draft.maintenance} onChange={(v) => setDraft({ ...draft, maintenance: v })} />
                <CostField label="Management %" value={draft.management} onChange={(v) => setDraft({ ...draft, management: v })} />
                <CostField label="No. of staff" value={draft.staffCount} onChange={(v) => setDraft({ ...draft, staffCount: v })} />
                <CostField label="Cost per hour £" value={draft.staffRate} onChange={(v) => setDraft({ ...draft, staffRate: v })} />
                <div className="bt-cost-actions">
                  <button type="button" className="bt-btn" onClick={saveCosts} disabled={saving}>Save</button>
                  {costScope && scopeHasOverride && <button type="button" className="bt-btn ghost" onClick={resetOverride} disabled={saving}>Use default</button>}
                </div>
              </div>
              {saveErr && <div className="bt-cost-err">{saveErr}</div>}
              <div className="bt-cost-note">
                {outlet === ALL
                  ? "This is the default every office inherits. Click an office tab above to give it its OWN staffing figures — accurate per-office costing needs each office set individually."
                  : `Editing ${outlet} only. Percentages are of that office's ticket revenue.`}{" "}
                Set a headcount <em>and</em> a cost per hour to charge staffing as hours worked instead of a % (the margin then becomes an outcome). Utilities/maintenance/management are fixed for the day; staffing is fixed for the day unless charged hourly.
              </div>
            </div>
          )}

          {/* Sales by month summary */}
          <MonthSummary view={view} fiscalYear={data.fiscalYear} />

          {/* Per-month hour bars — click a month to drill into its days */}
          <h3 className="bt-grid-h">Revenue &amp; contribution by hour <small>click a month to drill into its days · click an hour bar for its ticket types &amp; transactions</small></h3>
          <div className="bt-grid">
            {view.months.map((m) => {
              const open = expandedMonth === m.month;
              return (
                <div className={"bt-card" + (open ? " bt-card--open" : "")} key={m.month} style={open ? { gridColumn: "1 / -1" } : undefined}>
                  <button type="button" className="bt-card-btn" onClick={() => toggleMonth(m.month)} aria-expanded={open} title="Click to drill into this month's days">
                    <span>{open ? "▾ " : "▸ "}{fiscalMonthLabel(m.month, data.fiscalYear)}</span>
                    <span className="bt-card-sub">{money(m.net)} · {pct(m.net > 0 ? (m.profit / m.net) * 100 : null)}</span>
                  </button>
                  {open ? (
                    daysLoading && !dayRows[m.month] ? (
                      <div className="bt-empty" style={{ padding: 16 }}>Loading days…</div>
                    ) : dayView.length === 0 ? (
                      <div className="bt-empty" style={{ padding: 16 }}>No trading days in this month.</div>
                    ) : (
                      <div className="bt-daygrid">
                        {dayView.map((d) => (
                          <div className="bt-card bt-daycard" key={d.date}>
                            <div className="bt-card-head">
                              <span>{dayLabel(d.date)}</span>
                              <span className="bt-card-sub">{money(d.net)} · {pct(d.net > 0 ? (d.profit / d.net) * 100 : null)}</span>
                            </div>
                            <HourBars
                              slots={foldSlots(d.slots, allHours)}
                              onClick={(s) => openDetail({ slot: s.slot, bucket: s, month: m.month, date: d.date, label: `${dayLabel(d.date)} · ${foldedSlotLabel(s.slot)}` })}
                            />
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    <HourBars
                      slots={foldSlots(m.slots, allHours)}
                      onClick={(s) => openDetail({ slot: s.slot, bucket: s, month: m.month, date: null, label: `${fiscalMonthLabel(m.month, data.fiscalYear)} · ${foldedSlotLabel(s.slot)}` })}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Loss-making hours */}
          <LossList view={view} />
        </>
      )}

      {modal && <HourModal modal={modal} onClose={() => setModal(null)} />}
    </main>
  );
}

// --- Sales by month summary table -------------------------------------------
function MonthSummary({ view, fiscalYear }: { view: View; fiscalYear: number }) {
  const months = view.months;
  const cell = (v: string, neg?: boolean, tot?: boolean) => <td className={(neg ? "neg " : "") + (tot ? "tot" : "")}>{v}</td>;
  return (
    <div className="bt-summary">
      <h3>Sales by month</h3>
      <div className="bt-summary-wrap">
        <table className="bt-mt">
          <thead>
            <tr>
              <th className="rowlbl"></th>
              {months.map((m) => (
                <th key={m.month}>{fiscalMonthLabel(m.month, fiscalYear)}</th>
              ))}
              <th className="tot">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className="rowlbl">Ticket revenue</th>
              {months.map((m) => cell(money(m.net), false))}
              {cell(money(view.total.net), false, true)}
            </tr>
            <tr>
              <th className="rowlbl">Fixed cost</th>
              {months.map((m) => cell(money(m.fixed)))}
              {cell(money(view.total.fixed), false, true)}
            </tr>
            <tr>
              <th className="rowlbl">Contribution</th>
              {months.map((m) => cell(money(m.profit), m.profit < 0))}
              {cell(money(view.total.profit), view.total.profit < 0, true)}
            </tr>
            <tr className="muted">
              <th className="rowlbl">Margin</th>
              {months.map((m) => cell(pct(m.net > 0 ? (m.profit / m.net) * 100 : null), m.profit < 0))}
              {cell(pct(view.total.net > 0 ? (view.total.profit / view.total.net) * 100 : null), view.total.profit < 0, true)}
            </tr>
            <tr className="muted">
              <th className="rowlbl">Transactions</th>
              {months.map((m) => cell(m.transactions.toLocaleString("en-GB")))}
              {cell(view.total.transactions.toLocaleString("en-GB"), false, true)}
            </tr>
            <tr className="muted">
              <th className="rowlbl">Trading days</th>
              {months.map((m) => cell(String(m.tradingDays)))}
              {cell(String(view.totalTradingDays), false, true)}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="bt-mt-hint">Month labels run March to February (the NYMR operating year). Contribution = revenue − the staffing/overhead pot. Physical booking offices, walk-up sales.</p>
    </div>
  );
}

// --- small SVG hour-bar chart ----------------------------------------------
function HourBars({ slots, onClick }: { slots: SlotBucket[]; onClick: (s: SlotBucket) => void }) {
  if (slots.length === 0) return <div className="bt-empty" style={{ padding: 12 }}>No trade.</div>;
  const W = Math.max(200, slots.length * 26);
  const H = 78;
  const colW = W / slots.length;
  const maxNet = Math.max(0.01, ...slots.map((s) => s.net));
  const minProfit = Math.min(0, ...slots.map((s) => s.profit));
  const maxProfit = Math.max(0, ...slots.map((s) => s.profit));
  const topVal = Math.max(maxNet, maxProfit, 0.01);
  const botVal = Math.max(0, -minProfit);
  const total = topVal + botVal || 0.01;
  const y = (v: number) => ((topVal - v) / total) * H;
  const yZero = y(0);
  return (
    <svg className="bt-chart" viewBox={`0 0 ${W} ${H + 16}`} preserveAspectRatio="none" role="img">
      <line className="bt-zero" x1={0} x2={W} y1={yZero} y2={yZero} />
      {slots.map((s, i) => {
        const cx = i * colW + colW / 2;
        const salesW = Math.max(3, colW * 0.5);
        const profW = Math.max(2, colW * 0.26);
        const ySales = y(s.net);
        const profTop = s.profit >= 0 ? y(s.profit) : yZero;
        const profH = Math.abs(y(s.profit) - yZero);
        return (
          <g key={s.slot} className="bt-bar-click" onClick={() => onClick(s)}>
            <rect x={cx - salesW / 2} y={ySales} width={salesW} height={Math.max(0, yZero - ySales)} className="bt-bar-sales" />
            <rect x={cx - profW / 2} y={profTop} width={profW} height={Math.max(0.5, profH)} className={"bt-bar-prof" + (s.profit < 0 ? " neg" : "")} />
            {i % 2 === 0 && (
              <text className="bt-axis" x={cx} y={H + 11} textAnchor="middle">
                {s.slot === EDGE_EARLY ? "«" : s.slot === EDGE_LATE ? "»" : slotShortLabel(s.slot)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Kpi({ k, v, neg }: { k: string; v: string; neg?: boolean }) {
  return (
    <div className="bt-kpi">
      <div className="bt-kpi-k">{k}</div>
      <div className={"bt-kpi-v" + (neg ? " neg" : "")}>{v}</div>
    </div>
  );
}

function CostField({ label, value, onChange, dim }: { label: string; value: string; onChange: (v: string) => void; dim?: boolean }) {
  return (
    <label className={"bt-cost-field" + (dim ? " dim" : "")}>
      <span>{label}</span>
      <input type="number" inputMode="decimal" step="0.1" min="0" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function LossList({ view }: { view: View }) {
  const losers = view.slots.filter((s) => s.profit < 0).sort((a, b) => a.profit - b.profit);
  if (losers.length === 0) {
    return (
      <div className="bt-loss">
        <h3>Loss-making hours</h3>
        <div className="bt-empty" style={{ padding: 8 }}>No hour of the day runs at a loss for this scope. 🎉</div>
      </div>
    );
  }
  return (
    <div className="bt-loss">
      <h3>Loss-making hours (season, this scope)</h3>
      <table>
        <thead>
          <tr><th>Hour</th><th>Revenue</th><th>Fixed cost</th><th>Contribution</th><th>Txns</th></tr>
        </thead>
        <tbody>
          {losers.map((s) => (
            <tr key={s.slot}>
              <td>{foldedSlotLabel(s.slot)}</td>
              <td>{money(s.net)}</td>
              <td>{money(s.fixed)}</td>
              <td className="neg">{money(s.profit)}</td>
              <td>{s.transactions.toLocaleString("en-GB")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HourModal({ modal, onClose }: { modal: ModalState; onClose: () => void }) {
  const b = modal.bucket;
  const avg = b.transactions > 0 ? b.net / b.transactions : 0;
  const margin = b.net > 0 ? (b.profit / b.net) * 100 : null;
  return (
    <div className="bt-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="bt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bt-modal-head">
          <div>
            <div className="bt-modal-title">{modal.label}</div>
            <div className="bt-modal-sub">Booking-office hour detail</div>
          </div>
          <button type="button" className="bt-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="bt-modal-body">
          <div className="bt-stat-grid">
            <div className="bt-stat"><span>Ticket revenue</span><b>{money(b.net)}</b></div>
            <div className="bt-stat"><span>Fixed cost</span><b>{money(b.fixed)}</b></div>
            <div className="bt-stat"><span>Contribution</span><b>{money(b.profit)}</b></div>
            <div className="bt-stat"><span>Margin</span><b>{pct(margin)}</b></div>
            <div className="bt-stat"><span>Transactions</span><b>{b.transactions.toLocaleString("en-GB")}</b></div>
            <div className="bt-stat"><span>Avg £/txn</span><b>{money(avg)}</b></div>
          </div>

          {b.profit < 0 && (
            <div className="bt-cost-note" style={{ marginTop: 10, color: "#a33" }}>
              Negative because this hour's revenue ({money(b.net)}) is below the fixed staffing/overhead cost it carries ({money(b.fixed)}) — the office's daily pot shared across its trading hours. The ticket types below show what sold (often £0 concessions such as Kids Go Free / Return Visit Entry).
            </div>
          )}

          {/* Ticket types sold this hour */}
          <div style={{ marginTop: 14 }}>
            <div className="bt-modal-title" style={{ fontSize: 14 }}>Ticket types</div>
            {modal.bd == null || modal.bd.loading ? (
              <div className="bt-empty" style={{ padding: 10 }}>Loading…</div>
            ) : modal.bd.error ? (
              <div className="bt-empty err" style={{ padding: 10 }}>{modal.bd.error}</div>
            ) : !modal.bd.available ? (
              <div className="bt-cost-note">Ticket-type detail is held for the last two seasons only.</div>
            ) : modal.bd.rows.length === 0 ? (
              <div className="bt-cost-note">No ticket lines in this bucket.</div>
            ) : (
              <table className="bt-txn-table">
                <thead><tr><th>Ticket type</th><th>Group</th><th>Qty</th><th>Txns</th><th>Net</th></tr></thead>
                <tbody>
                  {modal.bd.rows.map((r, i) => (
                    <tr key={`${r.analysis}-${i}`}>
                      <td>{r.analysis}</td>
                      <td>{r.category}</td>
                      <td>{r.items}</td>
                      <td>{r.transactions}</td>
                      <td>{money(r.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bt-modal-title" style={{ fontSize: 14, marginTop: 16 }}>Transactions</div>
          {modal.txn == null ? null : modal.txn.loading ? (
            <div className="bt-empty" style={{ padding: 12 }}>Loading transactions…</div>
          ) : modal.txn.error ? (
            <div className="bt-empty err" style={{ padding: 12 }}>{modal.txn.error}</div>
          ) : !modal.txn.available ? (
            <div className="bt-cost-note" style={{ marginTop: 12 }}>Transaction detail is held for the last two seasons only.</div>
          ) : modal.txn.rows.length === 0 ? (
            <div className="bt-cost-note" style={{ marginTop: 12 }}>No individual transactions in this bucket.</div>
          ) : (
            <>
              <div className="bt-cost-note" style={{ marginTop: 12 }}>
                {modal.txn.total.toLocaleString("en-GB")} transactions · {money(modal.txn.totalNet)}{modal.txn.capped ? " · showing first 1,000" : ""}
              </div>
              <table className="bt-txn-table">
                <thead>
                  <tr><th>Date</th><th>Time</th><th>Office</th><th>Txn</th><th>Items</th><th>Net</th></tr>
                </thead>
                <tbody>
                  {modal.txn.rows.map((t, i) => (
                    <tr key={`${t.trans_no}-${i}`}>
                      <td>{t.date}</td>
                      <td>{t.time}</td>
                      <td>{t.location}</td>
                      <td>{t.trans_no}</td>
                      <td>{t.items}</td>
                      <td>{money(t.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Excel export -----------------------------------------------------------
type XlsxCell = { value: string | number | null; type?: typeof String | typeof Number; fontWeight?: "bold"; format?: string; align?: "left" | "right" };
const CUR = "£#,##0";

async function exportExcel(view: View, fyLabel: string, scope: string, allHours: boolean) {
  const monthNames = view.months.map((m) => fiscalMonthLabel(m.month, Number(`20${fyLabel.slice(-2)}`)));
  const hdr = (v: string, align?: "left" | "right"): XlsxCell => ({ value: v, type: String, fontWeight: "bold", align });
  const numC = (v: number): XlsxCell => ({ value: v, type: Number, format: CUR });
  const intC = (v: number): XlsxCell => ({ value: v, type: Number, format: "#,##0" });

  const summary: XlsxCell[][] = [
    [hdr(`Booking office by time — ${fyLabel} — ${scope}`)],
    [hdr(""), ...monthNames.map((n) => hdr(n, "right")), hdr("Total", "right")],
    [hdr("Ticket revenue"), ...view.months.map((m) => numC(m.net)), numC(view.total.net)],
    [hdr("Fixed cost"), ...view.months.map((m) => numC(m.fixed)), numC(view.total.fixed)],
    [hdr("Contribution"), ...view.months.map((m) => numC(m.profit)), numC(view.total.profit)],
    [hdr("Margin %"), ...view.months.map((m) => ({ value: m.net > 0 ? Math.round((m.profit / m.net) * 1000) / 10 : 0, type: Number, format: "0.0" } as XlsxCell)), { value: view.total.net > 0 ? Math.round((view.total.profit / view.total.net) * 1000) / 10 : 0, type: Number, format: "0.0" }],
    [hdr("Transactions"), ...view.months.map((m) => intC(m.transactions)), intC(view.total.transactions)],
    [hdr("Trading days"), ...view.months.map((m) => intC(m.tradingDays)), intC(view.totalTradingDays)],
  ];

  const hours = foldSlots(view.slots, allHours);
  const hoursSheet: XlsxCell[][] = [
    [hdr("Hour"), hdr("Ticket revenue", "right"), hdr("Fixed cost", "right"), hdr("Contribution", "right"), hdr("Margin %", "right"), hdr("Transactions", "right")],
    ...hours.map((s) => [
      { value: foldedSlotLabel(s.slot), type: String } as XlsxCell,
      numC(s.net), numC(s.fixed), numC(s.profit),
      { value: s.net > 0 ? Math.round((s.profit / s.net) * 1000) / 10 : 0, type: Number, format: "0.0" } as XlsxCell,
      intC(s.transactions),
    ]),
  ];

  const sheets = [{ data: summary, sheet: "By month" }, { data: hoursSheet, sheet: "By hour" }];
  const fileName = `booking-office-by-time-${fyLabel.replace("/", "-")}-${scope.replace(/[^\w]+/g, "_")}.xlsx`;
  const blob = await writeXlsxFile(sheets as never).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
